import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface AuthGuardProps {
  readonly children: ReactNode;
  readonly requiredRole?: string;
  readonly fallbackPath?: string;
}

export function AuthGuard({ 
  children, 
  requiredRole,
  fallbackPath = '/login' 
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check authentication - only redirect after loading is complete
  if (!isAuthenticated || !user) {
    // Redirect to login, preserving the current location
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Check role-based access if required
  if (requiredRole && user.role) {
    const hasRequiredRole = user.role === requiredRole;

    if (!hasRequiredRole) {
      return (
        <Navigate 
          to="/unauthorized" 
          state={{ from: location, requiredRole }} 
          replace 
        />
      );
    }
  }

  return <>{children}</>;
}
